import { useEffect, useState, useMemo, useRef } from 'react'
import { AlertTriangle, Eye, CreditCard, Check, Send, Trash2, Save, Undo2 } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { mensajeDeError } from '@/lib/errores'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import CargosServicios from '@/components/cd2b/CargosServicios'
import PaquetesCapacidad from '@/components/cd2b/PaquetesCapacidad'
import ComparadorEdiciones from '@/components/cd2b/ComparadorEdiciones'
import DescuentoInput from '@/components/cd2b/DescuentoInput'
import PreviewCotizacion from '@/components/cd2b/PreviewCotizacion'
import SelectorModulos from '@/components/cd2b/SelectorModulos'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { BloqueProrrateo, BloqueCobro, FilaImpacto } from './BloquesCobro'
import { ESTADO_ADDENDA, dinero, fecha } from './formato'
import LinkDePago from './LinkDePago'

/* ───────────────────── Editor de addenda (P15) ───────────────────── */

/**
 * Huella del paquete que arma el editor, para saber si lo que está en pantalla es lo que dice
 * la addenda guardada. Normaliza orden y tipos: dos paquetes iguales dan la misma huella.
 */
function huellaPaquete({ edicion, modulos, seats, capacidad, cargos, descuento }) {
  const desc = descuento && Number(descuento.valor) > 0 ? { tipo: descuento.tipo, valor: Number(descuento.valor) } : null
  return JSON.stringify({
    edicion: edicion || '',
    modulos: [...(modulos || [])].sort(),
    seats: { completo: Number(seats?.completo) || 0, operativo: Number(seats?.operativo) || 0 },
    capacidad: (capacidad || []).filter((c) => Number(c.cantidad) > 0)
      .map((c) => `${c.code}:${Number(c.cantidad)}`).sort(),
    cargos: (cargos || []).map((c) => `${c.code || c.name}:${Number(c.amount) || 0}:${c.frequency || ''}`).sort(),
    descuento: desc,
  })
}

export default function EditorAddenda({ ficha, onAplicada, onPaquetePendiente }) {
  const cat = useMemo(() => ficha.catalogo || {}, [ficha.catalogo])
  const { confirmar, dialogo } = useConfirmacion()
  const moneda = ficha.moneda || 'USD'
  const seatsDe = (ed) => (cat.ediciones || []).find((e) => e.edicion === ed)?.seatsIncluidos || { completo: 0, operativo: 0 }
  const codigosActuales = useMemo(() => (ficha.modulos || []).map((m) => m.code), [ficha.modulos])
  // Add-ons recurrentes vendibles (soporte, negocio adicional) — el mismo catálogo que pinta la lista.
  const addonsCat = useMemo(
    () => [
      ...(cat.addons?.soporte || []),
      // Canales de acceso (la app del teléfono): se marcan o no, como el soporte.
      ...(cat.addons?.acceso || []),
      ...(cat.addons?.negocio?.code ? [cat.addons.negocio] : []),
    ],
    [cat],
  )
  // 🔴 Una addenda NO se vive de una sentada: se genera, se le envía al dueño, y él la acepta
  // horas o días después. Mientras esto vivió solo en `useState(null)`, recargar la ficha la
  // borraba de la pantalla —seguía en la base y se veía como texto en el historial de abajo—,
  // pero el panel volvía a «Guardar cotización» y NO quedaba ningún botón para APLICAR lo que el
  // dueño ya había aceptado. La salida del asesor era generar otra addenda idéntica, y así la
  // empresa acumulaba addendas aceptadas que nadie podía aplicar. Se rehidrata con la pendiente.
  const pendientes = useMemo(
    () => (ficha.addendas || []).filter((a) => a.estado !== 'aplicada'),
    [ficha.addendas],
  )
  const pendiente = pendientes[0] || null // `listarAddendas` ordena de más nueva a más vieja
  // EN PRUEBA con cotización: el cambio se hace sobre la cotización (es lo que el cliente pagará
  // para pasar a Activa), así que el editor arranca de SU paquete, con sus servicios y su
  // descuento. Arrancar del de la sub y guardarlo encima le borraría a la cotización la
  // implementación y lo demás que no vive en la sub.
  const paqueteCotizacion = !pendiente ? ficha.propuestaOrigen?.paquete || null : null
  const origen = pendiente || paqueteCotizacion
  const [edicion, setEdicion] = useState(origen?.edicion || ficha.edicion || '')
  const [modulos, setModulos] = useState(
    pendiente
      ? (pendiente.modulos || []).map((m) => m.code).filter(Boolean)
      : paqueteCotizacion ? paqueteCotizacion.modulos || [] : codigosActuales,
  )
  // Asientos y add-ons arrancan en lo que la empresa YA tiene contratado (no en el cupo de la
  // edición ni en vacío): re-cotizar desde aquí no debe borrarle lo que ya paga.
  const [seats, setSeats] = useState(origen?.seats || ficha.seats || seatsDe(ficha.edicion))
  const [cargos, setCargos] = useState(() =>
    origen
      ? (origen.cargosAdicionales || [])
      : (ficha.addonsActuales || []).map((code) => {
        const a = addonsCat.find((x) => x.code === code)
        return { code, name: a?.label || code, amount: a?.precio || 0, frequency: 'monthly', origen: 'addon' }
      }),
  )
  // Paquetes de capacidad (productos/DTE) ya comprados: como los add-ons, se abre con lo que la
  // empresa realmente paga — re-cotizar desde aquí no debe quitarle cupo que ya compró.
  const [capacidad, setCapacidad] = useState(() => origen?.capacidad || ficha.capacidadActual || [])
  const [descuento, setDescuento] = useState(origen?.descuento || null) // descuento/promoción sobre el mensual
  // Precio mostrado: el de la addenda pendiente (ya calculado por el motor cuando se generó,
  // es lo que el dueño firmó) o el último facturado, mientras el panel de impacto en vivo no
  // haya corrido todavía. En ambos casos es solo el valor INICIAL — el useEffect de abajo lo
  // reemplaza por el que el motor calcule para el paquete que se esté armando ahora.
  const [precio, setPrecio] = useState(pendiente?.precioMensual ?? (ficha.mrr || ficha.precioPlan || 0))
  const [impacto, setImpacto] = useState(null)
  const [calculando, setCalculando] = useState(false)
  const [addenda, setAddenda] = useState(pendiente)
  // Link de pago vigente: el que se acaba de generar o el que la addenda ya tenía guardado. Se
  // pinta fijo en pantalla (no en un toast que se va) y mientras exista se sondea el pago.
  const linkGuardado = (a) => {
    if (a?.pago?.url && !a.pago.pagadoAt) {
      return { url: a.pago.url, neto: a.pago.neto, iva: a.pago.iva, total: a.pago.total, esPrueba: a.pago.esPrueba !== false }
    }
    if (a?.stripe?.paymentLinkUrl) return { url: a.stripe.paymentLinkUrl, total: null, esPrueba: false }
    return null
  }
  const [link, setLink] = useState(() => linkGuardado(pendiente))
  // ¿El dueño ya aceptó la addenda por el enlace del correo? Un recorte no se aplica sin eso:
  // apagarle áreas a un cliente también es un cambio de contrato.
  const aprobada = addenda?.estado === 'aprobada'
  const [trabajando, setTrabajando] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  // 🔴 17-oct (staging): con una addenda ya guardada, el asesor eligió Empresarial, el impacto se
  // recalculó… y «Generar link» cobró la addenda VIEJA (Micro + 4 módulos), que fue lo que se
  // aplicó. Lo que se ve tiene que ser lo que se cobra: se guarda la huella del paquete de la
  // addenda (al rehidratar, el estado ES el de la addenda; al guardar, el que se mandó) y, si el
  // editor se aparta de ella, el cobro y el envío se bloquean hasta guardar o deshacer.
  const huellaActual = huellaPaquete({ edicion, modulos, seats, capacidad, cargos, descuento })
  const huellaGuardada = useRef(pendiente ? huellaActual : null)
  const cambiosSinGuardar = !!addenda && huellaGuardada.current !== null && huellaActual !== huellaGuardada.current
  const usuario = useAuthStore((s) => s.user)
  const asesorNombre = [usuario?.firstName, usuario?.lastName].filter(Boolean).join(' ') || usuario?.name || ''

  // Datos normalizados para la vista previa que se le envía al dueño (sin comisión: es interno).
  const previewData = useMemo(() => {
    const edLabel = (cat.ediciones || []).find((e) => e.edicion === (impacto?.despues?.edicion || edicion))?.label || edicion
    const modulosActivos = (cat.modulos || [])
      .filter((m) => (impacto?.despues?.modulos || modulos).includes(m.code))
      .map((m) => m.label)
    return {
      tipo: 'addenda',
      empresa: ficha.empresaNombre || impacto?.empresaNombre || '',
      edicionLabel: edLabel,
      modulosActivos,
      desglose: impacto?.desglose || [],
      cargosUnicos: impacto?.cargosUnicosDetalle || [],
      totalMensual: impacto?.despues?.precioMensual || 0,
      totalUnico: impacto?.cargosUnicos || 0,
      descuento: impacto?.descuento || null,
      moneda,
      seats,
      asesor: asesorNombre,
    }
  }, [cat, impacto, edicion, modulos, ficha.empresaNombre, moneda, seats, asesorNombre])

  // Cambiar edición AUTO-marca los módulos que trae ese plan (Completa ⇒ todos los de paquete) y
  // rellena el cupo de asientos incluido. El asesor luego desmarca lo que no venderá.
  const cambiarEdicion = (ed) => {
    const inc = (cat.ediciones || []).find((e) => e.edicion === ed)?.modulosIncluidos || []
    // Conserva verticales/add-on ya marcados; suma los incluidos del nuevo plan.
    const optInMarcados = modulos.filter((c) => !(cat.modulos || []).find((m) => m.code === c)?.incluidoEn?.length)
    setEdicion(ed)
    setModulos([...new Set([...inc, ...optInMarcados])])
    setSeats(seatsDe(ed))
    // Emprendedor es gratis y nunca pasa por Stripe: el cliente no tiene tarjeta registrada, así
    // que cambiar a esa edición desmarca solo cualquier add-on de pago que quedara seleccionado.
    const permiteExtras = (cat.ediciones || []).find((e) => e.edicion === ed)?.permiteExtrasPago !== false
    if (!permiteExtras) {
      setCargos((c) => c.filter((x) => x.origen !== 'addon'))
      setCapacidad([])
    }
  }

  // Panel de impacto en vivo (lo computa el backend → nunca diverge de lo que factura). El
  // precio SIEMPRE lo deriva el motor del paquete armado; no hay override que mandar.
  useEffect(() => {
    let vivo = true
    setCalculando(true)
    const t = setTimeout(async () => {
      try {
        const payload = { edicion, modulos, seats, capacidad, cargosAdicionales: cargos, descuento }
        const res = await carteraService.impactoAddenda(ficha.subscriptionId, payload)
        if (vivo) {
          setImpacto(res?.data || null)
          // Las áreas del paquete en curso suben a la ficha para que la lista de ocultación de
          // abajo deje de ofrecer interruptores sobre áreas que esta propuesta ya no entrega.
          onPaquetePendiente?.(res?.data?.despues?.areas || null)
          if (res?.data?.despues?.precioMensual != null) setPrecio(res.data.despues.precioMensual)
        }
      } catch {
        if (vivo) { setImpacto(null); onPaquetePendiente?.(null) }
      } finally {
        if (vivo) setCalculando(false)
      }
    }, 400)
    return () => { vivo = false; clearTimeout(t) }
  }, [ficha.subscriptionId, edicion, modulos, seats, capacidad, cargos, descuento, onPaquetePendiente])


  const toggleModulo = (code) =>
    setModulos((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]))

  const hayCambio = impacto && (impacto.deltaPrecio !== 0 || impacto.areasAgregadas.length > 0 || impacto.areasQuitadas.length > 0)

  const generar = async () => {
    setTrabajando(true)
    try {
      const res = await carteraService.crearAddenda(ficha.subscriptionId, {
        edicion, modulos, seats, capacidad, cargosAdicionales: cargos,
        descuento: descuento && Number(descuento.valor) > 0 ? descuento : null,
        // El precio no se manda: el backend SIEMPRE lo deriva del paquete con el motor.
      })
      setAddenda(res?.data?.propuesta || null)
      huellaGuardada.current = huellaActual
      toast.success('Addenda generada')
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo generar'))
    } finally {
      setTrabajando(false)
    }
  }

  // Empresa en prueba con cotización editable: el cambio va a la cotización, sin cobrar.
  const cotizacionPrueba = !addenda ? impacto?.cotizacionOrigen || null : null

  /**
   * Aplica el paquete a la empresa EN PRUEBA: actualiza su cotización y la demo al momento. No hay
   * link: el cliente paga la cotización cuando quiera pasar a Activa. Si el dueño ya la había
   * aprobado, aprobó otro precio, y se le avisa al asesor antes de seguir.
   */
  const aplicarEnPrueba = async () => {
    if (cotizacionPrueba?.estado === 'aprobada') {
      const seguir = await confirmar({
        titulo: 'El dueño ya aprobó esta cotización',
        descripcion: `Aprobó ${dinero(cotizacionPrueba.precioMensual, moneda)}/mes. Con este cambio pasa a ${dinero(impacto?.despues?.precioMensual, moneda)}/mes: la cotización vuelve a borrador, el link de pago anterior deja de servir y tendrás que enviársela de nuevo para que la apruebe.`,
        textoConfirmar: 'Aplicar el cambio',
      })
      if (!seguir) return
    }
    setTrabajando(true)
    try {
      const res = await carteraService.cambiarPaqueteEnPrueba(ficha.subscriptionId, {
        edicion, modulos, seats, capacidad, cargosAdicionales: cargos,
        descuento: descuento && Number(descuento.valor) > 0 ? descuento : null,
      })
      const d = res?.data || {}
      if (d.demoSincronizada === false) {
        toast.error(`La cotización quedó actualizada, pero la empresa de prueba no recibió el cambio (${d.motivoSincronizacion || 'sin respuesta'}). Vuelve a aplicarlo en un momento.`, { duration: 12000 })
      } else {
        toast.success(d.volvioABorrador
          ? 'Cambio aplicado a la prueba. La cotización volvió a borrador: envíasela de nuevo al dueño.'
          : 'Cambio aplicado a la prueba. Se cobrará cuando el cliente pase a Activa.')
      }
      onAplicada()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo aplicar el cambio'))
    } finally {
      setTrabajando(false)
    }
  }

  /**
   * DESCARTA la addenda recién generada. Sin esto, cada clic en "Generar addenda" dejaba un
   * borrador vivo en la lista de cotizaciones —dos intentos sobre el mismo cliente y ahí se
   * quedaban los dos— y no había forma de quitarlos. Vuelve al editor con el paquete intacto,
   * así que el asesor puede corregir y volver a generar.
   *
   * Va por la ruta de ADDENDAS, no por la genérica de cotizaciones: aquella solo borra
   * borrador/rechazada/cancelada («ya salió al cliente: no se borra, se cancela»), que es la regla
   * correcta para un alta y la equivocada aquí — una addenda enviada o ya aceptada que el asesor
   * decide no aplicar tiene que poder quitarse, o se queda pendiente para siempre.
   */
  const descartarAddenda = async () => {
    const seguir = await confirmar({
      titulo: 'Descartar la addenda',
      descripcion: 'La addenda deja de existir y no se aplicará. El paquete que armaste se conserva para que puedas corregirlo.',
      textoConfirmar: 'Descartar',
      variante: 'destructiva',
    })
    if (!seguir) return
    setTrabajando(true)
    try {
      await carteraService.descartarAddenda(addenda._id)
      setAddenda(null)
      setLink(null)
      huellaGuardada.current = null
      toast.success('Addenda descartada')
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo descartar'))
    } finally {
      setTrabajando(false)
    }
  }

  /**
   * Guarda lo que está en pantalla como la addenda vigente: descarta la guardada y crea otra con
   * el paquete actual. Si ya había un link de pago, se avisa: Wompi no permite anularlo, así que
   * el cliente no debe usar el viejo (pagarlo ya no aplicaría nada).
   */
  const guardarCambios = async () => {
    const linkViejo = addenda?.pago?.url
    const seguir = await confirmar({
      titulo: 'Guardar los cambios del paquete',
      descripcion: linkViejo
        ? `Se reemplaza la addenda guardada por el paquete que tienes en pantalla. Ya generaste un link de pago de ${dinero(addenda.pago.total, moneda)} para la versión anterior: avísale al cliente que NO lo use y envíale el nuevo.`
        : 'Se reemplaza la addenda guardada por el paquete que tienes en pantalla.',
      textoConfirmar: 'Guardar cambios',
    })
    if (!seguir) return
    setTrabajando(true)
    try {
      await carteraService.descartarAddenda(addenda._id)
      setAddenda(null)
      setLink(null)
      huellaGuardada.current = null
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo reemplazar la addenda guardada'))
      setTrabajando(false)
      return
    }
    await generar()
  }

  /** Vuelve el editor al paquete de la addenda guardada. */
  const deshacerCambios = () => {
    if (!addenda) return
    setEdicion(addenda.edicion || ficha.edicion || '')
    setModulos((addenda.modulos || []).map((m) => m.code).filter(Boolean))
    setSeats(addenda.seats || seatsDe(addenda.edicion))
    setCargos(addenda.cargosAdicionales || [])
    setCapacidad(addenda.capacidad || [])
    setDescuento(addenda.descuento || null)
  }

  const aplicarRecorte = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.aplicarAddenda(addenda._id, {})
      // Si el precio no llegó a Stripe, el cliente se queda con el paquete nuevo y la factura
      // vieja. Es lo que hay que decir en voz alta, no enterrarlo en un log del servidor.
      const sync = r?.data?.stripeSync
      if (sync && sync.ok === false) {
        toast.error(`Cambio aplicado, pero el precio NO se actualizó en Stripe (${sync.motivo}). Revísalo antes de la próxima factura.`, { duration: 12000 })
      } else if (sync?.noAplica) {
        // Una demo, o un cliente que paga por transferencia, no tiene nada que alinear en la
        // pasarela. Antes esto salía como error rojo hablando de «la próxima factura» sobre una
        // factura que no existe: un cambio correcto parecía haber fallado.
        toast.success('Cambio aplicado. Esta empresa no se cobra por la pasarela, así que no hay nada que alinear.')
      } else if (sync?.prorrateado) {
        toast.success('Cambio aplicado. Stripe abonará lo no consumido en la próxima factura.')
      } else {
        toast.success('Cambio aplicado.')
      }
      onAplicada()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo aplicar'))
    } finally {
      setTrabajando(false)
    }
  }

  const generarLink = async () => {
    setTrabajando(true)
    try {
      const res = await carteraService.linkAddenda(addenda._id)
      const d = res?.data || {}
      if (d.url) {
        // El asesor solo ENVÍA el link (lo paga el cliente). Se copia de una vez, pero sobre todo
        // se queda A LA VISTA en el panel de abajo: el toast solo no se notaba.
        let copiado = false
        try { await navigator.clipboard.writeText(d.url); copiado = true } catch { /* noop */ }
        setLink({ url: d.url, neto: d.neto, iva: d.iva, total: d.total, esPrueba: !!d.esPrueba })
        setAddenda((a) => (a ? { ...a, estado: a.estado === 'borrador' ? 'enviada' : a.estado } : a))
        toast.success(copiado ? 'Link de pago listo y copiado. Envíaselo al cliente.' : 'Link de pago listo. Cópialo abajo y envíaselo al cliente.')
      }
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo generar el link'))
    } finally {
      setTrabajando(false)
    }
  }

  const enviarAlDuenoAddenda = async () => {
    setTrabajando(true)
    try {
      await carteraService.enviarAddendaAlDueno(addenda._id)
      // El envío mueve la addenda a 'enviada'. Sin reflejarlo aquí, el asesor seguiría viendo el
      // estado con el que se creó y no entendería por qué "Aplicar cambio" sigue bloqueado.
      setAddenda((a) => (a ? { ...a, estado: 'enviada' } : a))
      toast.success(
        addenda.cambio?.requierePago
          ? 'Cotización de upgrade enviada al dueño por correo'
          : 'Propuesta enviada al dueño. Cuando la acepte, podrás aplicarla.',
      )
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo enviar la cotización'))
    } finally {
      setTrabajando(false)
    }
  }

  // «Ya pagó» y «Ya lo aceptó» por fuera ya no son del asesor: la ampliación se aplica cuando la
  // pasarela confirma el cobro, y el recorte cuando el dueño lo acepta desde su enlace. Lo que
  // pase por otro medio lo concilia un administrador.

  return (
    <Card className="p-4 sm:p-5 border-primary-200 dark:border-primary-800">
      <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-3">Cambiar paquete</h3>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Configuración del nuevo paquete */}
        <div className="space-y-4">
          <div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Edición</p>
            <div className="flex flex-wrap gap-1.5">
              {(cat.ediciones || []).map((e) => (
                <button
                  key={e.edicion}
                  onClick={() => cambiarEdicion(e.edicion)}
                  className={`min-h-10 px-3 py-1.5 rounded-md text-body-sm border ${
                    edicion === e.edicion ? 'bg-primary text-white border-primary' : 'bg-card text-foreground border-line hover:bg-surface-muted'
                  }`}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </div>

          {/* Áreas base = lo que la edición abre sin ningún módulo/add-on vendible (núcleo,
              punto de venta, y en Micro también inventario básico). Emprendedor/Micro no
              tienen ningún módulo que las venda por separado (ver auditoría de áreas base del
              panel admin) — sin esto, "0 módulos vendidos" se leía como que no traen nada. */}
          {edicion && (
            <div>
              <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Áreas base de la edición</p>
              <div className="flex flex-wrap gap-1.5">
                {((cat.ediciones || []).find((e) => e.edicion === edicion)?.areas || []).map((a) => (
                  <Badge key={a} variant="secondary">{cat.areaMeta?.[a]?.label || a}</Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Módulos vendidos</p>
            <SelectorModulos
              variant="lista"
              modulos={cat.modulos || []}
              addons={addonsCat}
              edicion={edicion}
              seleccion={modulos}
              dependencias={impacto?.dependencias || []}
              onToggleModulo={toggleModulo}
              addonActivo={(a) => cargos.some((c) => c.code === a.code)}
              onToggleAddon={(a) => setCargos((prev) => prev.some((c) => c.code === a.code)
                ? prev.filter((c) => c.code !== a.code)
                : [...prev, { code: a.code, name: a.label, amount: a.precio, frequency: 'monthly', origen: 'addon' }])}
              dinero={(n) => dinero(n, moneda)}
              extrasBloqueados={(cat.ediciones || []).find((e) => e.edicion === edicion)?.permiteExtrasPago === false}
            />
          </div>

          <div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Precio mensual</p>
            <p className="text-heading-sm font-semibold text-neutral-900 dark:text-neutral-50">{dinero(precio, moneda)}</p>
            <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
              Calculado del paquete + asientos + servicios. Para negociar, usa el descuento de abajo.
            </p>
          </div>

          <div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Asientos (usuarios)</p>
            <div className="flex gap-3">
              <label className="text-caption text-neutral-600 dark:text-neutral-400">
                Completos
                <Input type="number" min="0" value={seats.completo}
                  onChange={(e) => setSeats((s) => ({ ...s, completo: Number(e.target.value) || 0 }))} className="mt-1 max-w-28" />
              </label>
              <label className="text-caption text-neutral-600 dark:text-neutral-400">
                Operativos (POS)
                <Input type="number" min="0" value={seats.operativo}
                  onChange={(e) => setSeats((s) => ({ ...s, operativo: Number(e.target.value) || 0 }))} className="mt-1 max-w-28" />
              </label>
            </div>
            <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">Incluye {seatsDe(edicion).completo} completos / {seatsDe(edicion).operativo} operativos; los extra suman.</p>
          </div>

          <div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Cupo de productos y DTE</p>
            <PaquetesCapacidad catalogo={cat} capacidad={capacidad} setCapacidad={setCapacidad}
              limites={impacto?.despues?.limites} moneda={moneda} />
          </div>

          <div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Servicios y cargos adicionales</p>
            <CargosServicios catalogo={cat} cargos={cargos} setCargos={setCargos} moneda={moneda} />
          </div>

          <DescuentoInput descuento={descuento} setDescuento={setDescuento} moneda={moneda}
            montoDescuento={impacto?.descuento?.monto || 0} precioFull={impacto?.descuento?.precioFull || 0} />

          <ComparadorEdiciones catalogo={cat} edicionActual={edicion}
            modulosSel={modulos} moneda={moneda} onSubir={cambiarEdicion} />
        </div>

        {/* Panel de impacto */}
        <div className="bg-neutral-50 dark:bg-neutral-800/60 rounded-lg p-4">
          <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-3">Impacto</p>
          {calculando && !impacto ? (
            <Skeleton className="h-24" />
          ) : impacto ? (
            <div className="space-y-3">
              <FilaImpacto label="Precio" antes={impacto.antes.precioMensual} despues={impacto.despues.precioMensual} moneda={impacto.moneda} />
              {impacto.comisiona ? (
                <FilaImpacto label="Tu comisión / mes" antes={impacto.antes.comisionMensual} despues={impacto.despues.comisionMensual} moneda={impacto.moneda} />
              ) : impacto.enPrueba ? (
                // En prueba todavía no hay venta: la comisión nace al pagar, no es que no la haya.
                <p className="text-caption text-neutral-500 dark:text-neutral-400">La comisión se calcula cuando el cliente pague.</p>
              ) : (
                <p className="text-caption text-neutral-500 dark:text-neutral-400">Esta empresa la atiendes pero no la comisionas.</p>
              )}

              {/* Desglose de lo que se ORDENA (plan base + módulos extra + asientos + add-ons + cargos
                  recurrentes), para que el cliente vea qué compone el precio mensual, no solo el total.
                  Reventa cd-2b (docs/120 F8): si este asesor vende con margen propio, la MISMA tabla
                  gana dos columnas (tu costo / tu margen) que SOLO él ve — `impacto.despues.precioCongelado`
                  ya trae ese desglose línea por línea (F2, `aplicarMargen`), sin pedir nada nuevo al
                  backend. Si no es reventa, la tabla se ve EXACTAMENTE igual que hoy (F-02: nada
                  cambia para consultores de comisión fija). */}
              {impacto.despues?.modo === 'reventa' && impacto.despues?.precioCongelado?.lineas?.length > 0 ? (
                <div className="rounded-md border border-line bg-card divide-y divide-neutral-100 dark:divide-neutral-800">
                  <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase px-3 pt-2 pb-1">Detalle del paquete (mensual) — solo tú lo ves</p>
                  {/* Cuatro columnas no caben en un teléfono: el detalle se desliza de lado dentro
                      de su recuadro en vez de estrujar el concepto o empujar la página. */}
                  <div className="overflow-x-auto">
                  <div className="min-w-[22rem] divide-y divide-neutral-100 dark:divide-neutral-800">
                  <div className="flex items-center gap-2 px-3 py-1 text-micro text-neutral-400 dark:text-neutral-500 uppercase">
                    <span className="flex-1">Concepto</span>
                    <span className="w-20 text-right">Tu costo</span>
                    <span className="w-20 text-right">Tu margen</span>
                    <span className="w-20 text-right">Cliente</span>
                  </div>
                  {impacto.despues.precioCongelado.lineas.map((l, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="text-caption text-neutral-700 dark:text-neutral-300 flex-1 min-w-0">{l.concepto}</span>
                      <span className="text-caption tabular-nums text-neutral-600 dark:text-neutral-400 w-20 text-right">{dinero(l.costo, impacto.moneda)}</span>
                      <span className="text-caption tabular-nums text-accent-700 dark:text-accent-300 w-20 text-right">{l.margen >= 0 ? '+' : ''}{dinero(l.margen, impacto.moneda)}</span>
                      <span className="text-caption font-medium tabular-nums text-neutral-800 dark:text-neutral-200 w-20 text-right">{dinero(l.venta, impacto.moneda)}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-50 dark:bg-neutral-800/60">
                    <span className="text-caption font-medium text-neutral-600 dark:text-neutral-400 flex-1">Total mensual</span>
                    <span className="text-caption tabular-nums text-neutral-700 dark:text-neutral-300 w-20 text-right">{dinero(impacto.despues.precioBaseMensual, impacto.moneda)}</span>
                    <span className="text-caption tabular-nums text-accent-700 dark:text-accent-300 w-20 text-right">+{dinero(impacto.despues.margenMensual, impacto.moneda)}</span>
                    <span className="text-body-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-50 w-20 text-right">{dinero(impacto.despues.precioMensual, impacto.moneda)}</span>
                  </div>
                  </div>
                  </div>
                  <p className="text-micro text-neutral-400 dark:text-neutral-500 px-3 py-1.5">
                    {ficha.empresaNombre || 'Este cliente'} nunca ve estas dos columnas — su documento y su checkout solo muestran la última.
                  </p>
                </div>
              ) : impacto.desglose?.length > 0 && (
                <div className="rounded-md border border-line bg-card divide-y divide-neutral-100 dark:divide-neutral-800">
                  <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase px-3 pt-2 pb-1">Detalle del paquete (mensual)</p>
                  {impacto.desglose.map((l, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
                      <span className="text-caption text-neutral-700 dark:text-neutral-300">
                        {l.concepto}
                        {l.detalle ? <span className="text-neutral-400 dark:text-neutral-500"> · {l.detalle}</span> : null}
                      </span>
                      <span className="text-caption font-medium tabular-nums text-neutral-800 dark:text-neutral-200">
                        {l.precio > 0 ? dinero(l.precio, impacto.moneda) : <span className="text-success-700 dark:text-success-300">Incluido</span>}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-neutral-50 dark:bg-neutral-800/60">
                    <span className="text-caption font-medium text-neutral-600 dark:text-neutral-400">Total mensual</span>
                    <span className="text-body-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{dinero(impacto.despues.precioMensual, impacto.moneda)}</span>
                  </div>
                </div>
              )}

              {/* Cargos de una sola vez (implementación, migración, servicios): itemizados aparte. */}
              {impacto.cargosUnicos > 0 && (
                <div className="rounded-md border border-warning-200 dark:border-warning-800 bg-warning-50/60 dark:bg-warning/15 px-3 py-2">
                  <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase mb-1.5">Cargos únicos (primera factura)</p>
                  <div className="space-y-1">
                    {(impacto.cargosUnicosDetalle || []).map((c, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <span className="text-caption text-neutral-600 dark:text-neutral-400">{c.name || 'Cargo'}</span>
                        <span className="text-caption tabular-nums text-neutral-800 dark:text-neutral-200">{dinero(c.amount, impacto.moneda)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between gap-2 pt-1 border-t border-warning-200/70 dark:border-warning-800/70">
                      <span className="text-caption font-medium text-neutral-700 dark:text-neutral-300">Total una vez</span>
                      <span className="text-body-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{dinero(impacto.cargosUnicos, impacto.moneda)}</span>
                    </div>
                  </div>
                </div>
              )}
              {/* Módulos que el asesor NO marcó y entraron por dependencia. Van ANTES de
                  "Enciende" porque explican un renglón de COBRO: sin esto, el total sube y
                  parece un fallo de la aplicación. */}
              {impacto.dependencias?.length > 0 && (
                <p className="text-caption text-warning-700 dark:text-warning-300 flex items-start gap-1.5">
                  <span className="font-medium">Se añade por dependencia:</span>{' '}
                  {impacto.dependencias.map((d) => `${d.label} (lo exige ${d.requeridoPorLabel})`).join(', ')}
                </p>
              )}
              {/* Áreas del PLAN que el paquete deja apagadas. Es la fuga silenciosa opuesta:
                  vender solo verticales sobre Micro deja al cliente sin el Inventario que ese
                  plan vende, y hasta ahora nada lo decía. */}
              {impacto.areasRecortadas?.length > 0 && (
                <p className="text-caption text-danger-700 dark:text-danger-300 flex items-start gap-1.5">
                  <span className="font-medium">El plan NO entregará:</span>{' '}
                  {impacto.areasRecortadas.map((a) => a.label).join(', ')} · no se vendió el módulo que las trae
                </p>
              )}
              {impacto.areasAgregadas.length > 0 && (
                <p className="text-caption text-success-700 dark:text-success-300 flex items-start gap-1.5">
                  <span className="font-medium">Enciende:</span> {impacto.areasAgregadas.map((a) => a.label).join(', ')}
                </p>
              )}
              {impacto.areasQuitadas.length > 0 && (
                <p className="text-caption text-danger-700 dark:text-danger-300 flex items-start gap-1.5">
                  <span className="font-medium">Apaga:</span> {impacto.areasQuitadas.map((a) => a.label).join(', ')}
                </p>
              )}
              {/* Áreas que el cliente tiene y que ya no se venden: no están en la lista de arriba,
                  así que el asesor no puede marcarlas ni desmarcarlas. Se dicen para que sepa que
                  siguen ahí — antes desaparecían del paquete anunciadas como "Apaga:". */}
              {(impacto.areasConservadas || []).length > 0 && (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 flex items-start gap-1.5">
                  <span className="font-medium">Se conserva:</span>{' '}
                  {impacto.areasConservadas.map((a) => a.label).join(', ')} · ya no se vende, no se cobra y no se apaga
                </p>
              )}
              {/* La etiqueta sale de `clase`, que la calcula el motor por el DINERO. Antes se
                  deducía de `requierePago`, y un paquete que añadía un área pero costaba MENOS
                  se anunciaba como "Ampliación · requiere pago" y no había forma de aplicarlo. */}
              {!hayCambio ? (
                <Badge variant="secondary">Sin cambios</Badge>
              ) : impacto.enPrueba ? (
                <Badge variant="success">Prueba · sin cobro</Badge>
              ) : impacto.clase === 'ampliacion' ? (
                <Badge variant="warning">Ampliación · requiere pago</Badge>
              ) : impacto.clase === 'recorte' ? (
                <Badge variant="success">
                  {impacto.prorrateo?.motivo === 'cobro_por_enlace'
                    ? 'Recorte · rige desde la próxima renovación'
                    : 'Recorte · Stripe abona lo no consumido'}
                </Badge>
              ) : (
                <Badge variant="secondary">Ajuste · mismo importe</Badge>
              )}
              {/* La etiqueta anuncia un prorrateo; aquí va la CIFRA. Y cuando no hay ninguno
                  —una demo no tiene suscripción en la pasarela— se dice, en vez de dejar la
                  promesa de "Stripe abona lo no consumido" colgando sobre algo que no existe. */}
              {hayCambio && !impacto.enPrueba && <BloqueProrrateo p={impacto.prorrateo} moneda={impacto.moneda} cargoNeto={impacto.cargoNeto} />}
              {hayCambio && impacto.enPrueba && (
                <p className="text-caption text-neutral-600 dark:text-neutral-400">
                  La empresa está en prueba: el cambio se aplica ya y no se cobra ahora. Se paga
                  {' '}{dinero(impacto.despues.precioMensual, impacto.moneda)}/mes cuando el cliente pase a Activa.
                </p>
              )}
              <BloqueCobro c={impacto.cobro} moneda={impacto.moneda} />
            </div>
          ) : (
            <p className="text-caption text-neutral-500 dark:text-neutral-400">Ajusta el paquete para ver el impacto.</p>
          )}
        </div>
      </div>

      {/* Acciones */}
      <div className="mt-5 pt-4 border-t border-neutral-100 dark:border-neutral-800">
        {cotizacionPrueba ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button onClick={aplicarEnPrueba} disabled={!hayCambio || trabajando}>
                <Check size={15} className="mr-1.5" /> Aplicar a la prueba
              </Button>
              <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={!impacto}>
                <Eye size={15} className="mr-1.5" /> Vista previa
              </Button>
            </div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400">
              {hayCambio
                ? 'Actualiza la cotización y la empresa de prueba al momento, sin link de pago. Cuando el cliente quiera pasar a Activa, envíale el cobro desde Gestión.'
                : 'Cambia edición, módulos, asientos o cupos para poder aplicar el cambio.'}
            </p>
          </div>
        ) : !addenda ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {/* Este botón ES el "guardar": la addenda es la cotización del cambio, guardada y
                  con su propio número. Se llamaba solo "Generar addenda" y no se leía como tal. */}
              <Button onClick={generar} disabled={!hayCambio || trabajando}>
                <Save size={15} className="mr-1.5" /> Guardar cotización (addenda)
              </Button>
              <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={!impacto}>
                <Eye size={15} className="mr-1.5" /> Vista previa
              </Button>
            </div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400">
              {hayCambio
                ? (impacto?.requierePago
                  ? 'Guarda el cambio como addenda. Después podrás enviársela al dueño y generar el link de pago para que lo pague.'
                  : 'Guarda el cambio como addenda. Después podrás enviársela al dueño para que la acepte y aplicarla.')
                : 'Cambia edición, módulos, asientos o cupos para poder guardar la cotización.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Que se vea que esto es un DOCUMENTO guardado y en qué punto está: sin esto, una
                addenda rehidratada al abrir la ficha parecía algo que el asesor acababa de armar. */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={(ESTADO_ADDENDA[addenda.estado] || {}).variant || 'secondary'}>
                {(ESTADO_ADDENDA[addenda.estado] || {}).label || addenda.estado}
              </Badge>
              <span className="text-micro text-neutral-500 dark:text-neutral-400">
                Addenda del {fecha(addenda.createdAt)}
                {addenda.aceptacion?.aceptadaAt && ` · aceptada por ${addenda.aceptacion.nombreFirmante || 'el dueño'} el ${fecha(addenda.aceptacion.aceptadaAt)}`}
              </span>
            </div>
            <p className="text-caption text-neutral-600 dark:text-neutral-400">
              Guardada: <span className="font-medium text-neutral-800 dark:text-neutral-200">
                {(cat.ediciones || []).find((e) => e.edicion === addenda.edicion)?.label || addenda.edicion}
              </span>
              {' · '}{(addenda.modulos || []).length} módulo{(addenda.modulos || []).length === 1 ? '' : 's'}
              {' · '}{dinero(addenda.precioMensual, moneda)}/mes
            </p>
            {cambiosSinGuardar && (
              <div role="alert" className="rounded-md border border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20 p-3 space-y-2">
                <p className="text-caption text-warning-800 dark:text-warning-300 flex items-start gap-1.5">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  Tienes cambios sin guardar. El link de pago y el envío al dueño usarían la addenda guardada,
                  no lo que ves en pantalla. Guarda los cambios o deshazlos.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={guardarCambios} disabled={trabajando || !hayCambio}>
                    <Save size={15} className="mr-1.5" /> Guardar cambios
                  </Button>
                  <Button variant="outline" onClick={deshacerCambios} disabled={trabajando}>
                    <Undo2 size={15} className="mr-1.5" /> Deshacer cambios
                  </Button>
                </div>
                {!hayCambio && (
                  <p className="text-micro text-warning-800 dark:text-warning-300">
                    El paquete en pantalla es igual al que la empresa ya tiene: no hay addenda que guardar. Descarta la guardada si ya no aplica.
                  </p>
                )}
              </div>
            )}
            {pendientes.length > 1 && (
              <p className="text-caption text-warning-700 dark:text-warning-300 flex items-start gap-1.5">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                Esta empresa tiene {pendientes.length} addendas sin aplicar. Aquí se muestra la más
                reciente; descarta las anteriores desde el historial para no aplicar un paquete viejo.
              </p>
            )}
            {addenda.cambio?.requierePago ? (
              <>
                <p className="text-caption text-neutral-600 dark:text-neutral-400 flex items-center gap-1.5">
                  <CreditCard size={13} className="shrink-0" /> Es una ampliación: el cliente paga el prorrateo antes de encender lo nuevo.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={enviarAlDuenoAddenda} disabled={trabajando || cambiosSinGuardar}>
                    <Send size={15} className="mr-1.5" /> Enviar al dueño
                  </Button>
                  <Button onClick={generarLink} disabled={trabajando || cambiosSinGuardar}>
                    <CreditCard size={15} className="mr-1.5" /> {link ? 'Regenerar link de pago' : 'Generar link de pago'}
                  </Button>
                </div>
                {link && !cambiosSinGuardar && (
                  <LinkDePago addendaId={addenda._id} link={link} moneda={moneda} onPagada={onAplicada} />
                )}
                <p className="text-micro text-neutral-500 dark:text-neutral-400">«Enviar al dueño» le manda la cotización del upgrade por correo para que la revise y acepte antes de cobrar.</p>
              </>
            ) : (
              <>
                <p className="text-caption text-neutral-600 dark:text-neutral-400 flex items-start gap-1.5">
                  <Send size={13} className="mt-0.5 shrink-0" />
                  {addenda.cambio?.enPrueba
                    ? 'La empresa está en prueba: el cambio no se cobra.'
                    : addenda.cambio?.clase === 'recorte'
                    ? (impacto?.prorrateo?.motivo === 'cobro_por_enlace'
                      ? 'El cliente pasa a pagar menos desde su próxima renovación. Lo ya pagado no se devuelve.'
                      : 'El cliente pasa a pagar menos. Al aplicarlo, Stripe le abona lo no consumido del ciclo que ya pagó.')
                    : 'El paquete cambia sin mover el importe. No hay nada que cobrar.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={enviarAlDuenoAddenda} disabled={trabajando || cambiosSinGuardar}>
                    <Send size={15} className="mr-1.5" /> Enviar al dueño
                  </Button>
                  <Button onClick={aplicarRecorte} disabled={trabajando || (!aprobada && !addenda.cambio?.enPrueba) || cambiosSinGuardar}>
                    <Check size={15} className="mr-1.5" /> Aplicar cambio
                  </Button>
                </div>
                <p className="text-micro text-neutral-500 dark:text-neutral-400">
                  {addenda.cambio?.enPrueba
                    ? 'La empresa está en prueba: puedes aplicarlo sin esperar la aceptación del dueño.'
                    : aprobada
                    ? 'El dueño ya la aceptó: puedes aplicarla cuando la implementación esté lista.'
                    : 'Apagarle áreas a un cliente también es un cambio de contrato: envíasela y aplícala cuando la acepte desde su enlace.'}
                </p>
              </>
            )}
            <Button variant="ghost" onClick={descartarAddenda} disabled={trabajando}>
              <Trash2 size={15} className="mr-1.5" /> Descartar addenda
            </Button>
            <p className="text-micro text-neutral-500 dark:text-neutral-400 flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              La licencia firmada dura 7 días: el recorte puede tardar en verse en la app del cliente.
            </p>
          </div>
        )}
      </div>

      <PreviewCotizacion open={previewOpen} onClose={() => setPreviewOpen(false)} {...previewData} />
      {dialogo}
    </Card>
  )
}
